import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LibraryPage, ReportConfig, TemplateImage } from '@modules/report-generator/interfaces';
import { ImageElement } from '@modules/report-generator/interfaces/elements';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SelectImageDialogComponent } from '../../shared/select-image-dialog/select-image-dialog.component';
import { UniquePageKey } from '@modules/report-generator/shared';
import { String } from 'typescript-string-operations';
import { AppConstants } from '@core/constants';
import { SaveTemplateFormData } from '@shared/interfaces/save-template-dialog.interface';
import { SaveTemplateDialogComponent } from '../../shared/save-template-dialog/save-template-dialog.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

@Component({
  selector: 'app-report-image',
  templateUrl: './report-image.component.html',
  styleUrls: ['./report-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportImageComponent implements OnChanges {
  private readonly store = inject(ReportGeneratorStore);
  private snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  @Input() element!: ImageElement;
  @Input() reportConfig!: ReportConfig;
  @Input() page!: LibraryPage;

  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private svgCache = new Map<string, SafeUrl>();

  imageSrc: SafeUrl | string = '';
  orgLogoLoaded: boolean = false;
  imageLoaded: boolean = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['element'] || changes['reportConfig']) {
      if (this.element.type === 'org-logo') {
        this.orgLogoLoaded = Boolean(
          this.reportConfig?.organization?.photo || this.reportConfig?.organizationPhoto?.size
        );
      } else if (this.element.type === 'image') {
        if (this.element.isReplaceable) {
          this.imageLoaded = true;
          this.loadImage();
          return;
        }

        const imageData = this.element.data?.data;
        if (!imageData) {
          return;
        }
        const imageLoading = imageData.includes('loading.png');
        if (imageLoading) {
          this.imageLoaded = false;
        } else {
          this.imageLoaded = true;
          this.loadImage();
        }
      }
    }
  }

  get needsImageReplacement(): boolean {
    return !!this.element.isReplaceable && !this.getTemplateImageIdOverride() && !this.element.data?.data;
  }

  get canReplaceImage(): boolean {
    return !!this.element.isReplaceable && !!this.element.data?.data;
  }

  get canHideImage(): boolean {
    return !!this.element.isReplaceable && !!this.element.data?.data;
  }

  get isImageHidden(): boolean {
    return !!this.element.isReplaceable && this.getHideImageOverride();
  }

  getOrgLogo(): SafeUrl | string {
    if (this.reportConfig?.organizationPhoto?.base64Data) {
      const base64Data = this.reportConfig.organizationPhoto.base64Data;
      const contentType = this.reportConfig.organizationPhoto.contentType;
      const dataUrl = base64Data.startsWith('data:') ? base64Data : `data:${contentType};base64,${base64Data}`;
      return this.sanitizer.bypassSecurityTrustUrl(dataUrl);
    }
    return this.reportConfig?.organization?.photo || 'assets/images/app-logo-header.png';
  }

  getOrgPhotoSizePercent(): number {
    return 90;
  }

  onReplaceableImageClick(): void {
    if (this.store.isGlobalTemplate()) {
      this.snackBar.open('Template has to be saved before uploading images', 'OK', AppConstants.TOAST_CONFIG.WARNING);
      this.saveTemplate();
      return;
    }

    const dialogRef: MatDialogRef<SelectImageDialogComponent> = this.dialog.open(SelectImageDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        images: this.reportConfig.template?.images ?? [],
        templateId: this.reportConfig.template?.id,
        imagesUsedInTemplate: this.imagesProtectedFromDeletion(),
        canHideImage: this.canHideImage,
      },
    });

    dialogRef.afterClosed().subscribe((selectedImageId: number | null | undefined) => {
      if (selectedImageId == null) return; // cancelled

      if (selectedImageId === -1) {
        this.store.clearImageOverride(UniquePageKey(this.page), this.element.slug);
        return;
      }

      if (selectedImageId === -2) {
        this.store.hideImageOverride(UniquePageKey(this.page), this.element.slug);
        return;
      }

      if (selectedImageId === -3) {
        this.store.clearImageOverride(UniquePageKey(this.page), this.element.slug);
        return;
      }

      this.store.overrideImage(UniquePageKey(this.page), this.element.slug, selectedImageId);
    });
  }

  private saveTemplate(): void {
    const dialogRef: MatDialogRef<SaveTemplateDialogComponent> = this.dialog.open(SaveTemplateDialogComponent, {
      width: '480px',
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: true,
      data: {
        templateName: this.reportConfig?.template?.name!,
        isGlobal: !this.reportConfig?.template?.organization,
      },
    });

    dialogRef.afterClosed().subscribe((result: SaveTemplateFormData) => {
      if (!result) {
        return;
      }

      const newTemplate = {
        ...this.reportConfig?.template!,
        name: result.templateName,
      };
      this.store.saveTemplate(newTemplate, result.shouldClone);
    });
  }

  private imagesProtectedFromDeletion(): number[] {
    const savedIds = this.reportConfig.template?.savedTemplateImageIds ?? [];
    const sessionIds = this.imagesUsedInTemplate();
    return [...new Set([...savedIds, ...sessionIds])];
  }

  private imagesUsedInTemplate(): number[] {
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    if (!templateOverrides) return [];

    const imageIds: number[] = [];
    Object.values(templateOverrides).forEach((pageOverride: Record<string, Record<string, any>>) => {
      Object.values(pageOverride).forEach((elementOverride) => {
        if (elementOverride?.templateImageId != null) {
          imageIds.push(elementOverride.templateImageId);
        }
      });
    });

    return imageIds;
  }

  private loadImage(): void {
    // Check if this is a theme decoration (slug pattern: theme-decoration-*)
    if (this.element.slug?.startsWith('theme-decoration-')) {
      this.loadThemeDecorationImage();
    } else {
      this.loadStandardImage();
    }
  }

  private loadThemeDecorationImage(): void {
    const imagePath = this.element.data?.data;
    if (!imagePath) {
      return;
    }

    // Extract color property from slug or element metadata
    const colorProperty = this.element.themeColor ?? 'primary';
    const themeColor = colorProperty
      ? this.reportConfig?.template?.theme?.colors?.[
          colorProperty as keyof typeof this.reportConfig.template.theme.colors
        ]
      : undefined;

    this.loadAndRecolorSvg(imagePath, themeColor);
  }

  private getTemplateImageIdOverride(): number | undefined {
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    return templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.templateImageId;
  }

  private loadStandardImage(): void {
    const templateImageId = this.getTemplateImageIdOverride();
    if (templateImageId) {
      this.resolveTemplateImage(templateImageId);
      return;
    }

    if (this.element.data?.type === 'url') {
      this.imageSrc = this.element.data.data;
    } else if (this.element.data?.type === 'upload') {
      this.imageSrc = this.element.data.data;
    }
  }

  private loadAndRecolorSvg(svgPath: string, themeColor?: string): void {
    const cacheKey = `${svgPath}::${themeColor}`;

    if (this.svgCache.has(cacheKey)) {
      this.imageSrc = this.svgCache.get(cacheKey)!;
      return;
    }

    fetch(svgPath)
      .then((response) => response.text())
      .then((svgText) => {
        const recoloredSvg = this.recolorSvg(svgText, themeColor);
        const blob = new Blob([recoloredSvg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);

        this.svgCache.set(cacheKey, safeUrl);
        this.imageSrc = safeUrl;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Failed to load theme decoration SVG:', error);
      });
  }

  private recolorSvg(svg: string, color?: string): string {
    if (!color) {
      return svg;
    }

    // Check if SVG has gradients (indicated by url(#...) fill references)
    const hasGradient = /fill="url\(#[^)]+\)"/.test(svg);

    if (hasGradient) {
      // Recolor gradient stops, preserving opacity and fade effects
      return svg.replace(/stop-color="[^"]*"/g, `stop-color="${color}"`);
    } else {
      // Replace fill attributes
      let recolored = svg.replace(/fill="[^"]*"/g, `fill="${color}"`);
      // Replace stroke attributes if needed
      recolored = recolored.replace(/stroke="[^"]*"/g, `stroke="${color}"`);

      return recolored;
    }
  }

  private resolveTemplateImage(templateImageId: number): void {
    const templateId = this.reportConfig.template?.id;
    let apiUrl = String.Format(AppConstants.API.REPORTING.TEMPLATES.UPLOAD_IMAGE + '{1}/', templateId, templateImageId);
    const params = new URLSearchParams({ imageData: 'true' });
    apiUrl += `?${params.toString()}`;

    fetch(`${AppConstants.API_HOST}${apiUrl}`, { credentials: 'include' })
      .then((response) => response.json())
      .then(({ base64Data, contentType }: TemplateImage) => {
        this.imageSrc = `data:${contentType};base64,${base64Data}`;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Failed to load template image:', error);
      });
  }

  private getHideImageOverride(): boolean {
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    return templateOverrides?.[UniquePageKey(this.page)]?.[this.element.slug]?.hideImage ?? false;
  }
}
